import { useCallback } from 'react';
import mixpanel from 'mixpanel-browser';

import { Popup } from 'web-components';
import { Flex, Text, SVG } from 'web-styled';

import type { Action } from './types';
import * as S from './styled';

const ADD_ICONS = {
  client: SVG.Clients,
  exercise: SVG.Exercise,
  workout: SVG.KettleBell,
  program: SVG.Program,
  video: SVG.VideoLibrary,
  announcement: SVG.Announcement,
} as const;

type Props = {
  isOpen: boolean;
  addActions: Action[];
  onToggle: () => void;
  onClose: () => void;
};

const AddActionsDropdown = ({
  isOpen,
  addActions,
  onToggle,
  onClose,
}: Props) => {
  const handleToggle = useCallback(() => {
    onToggle();
    if (!isOpen) {
      mixpanel.track('Clicked universal add button');
    }
  }, [isOpen, onToggle]);

  const handleActionClick = useCallback((action: Action) => {
    action.action();
    onClose();
  }, [onClose]);

  return (
    <S.Dropdown isOpen={isOpen}>
      <Popup
        isOpen={isOpen}
        trigger={(
          <S.Trigger isOpen={isOpen}>
            <Flex column align="center" spacing={5}>
              <S.PlusCircle isOpen={isOpen} />
              <Text
                color={isOpen ? '#ffffff' : '#757575'}
                size="10px"
                lineHeight="11px"
                whiteSpace="pre-line"
              >
                Add
              </Text>
            </Flex>
          </S.Trigger>
        )}
        setPopupVisible={handleToggle}
        onClickOutside={onClose}
      >
        <S.Actions mr={116} minWidth="180px">
          {addActions.map((action) => {
            const IconComponent = ADD_ICONS[action.icon as keyof typeof ADD_ICONS];

            return (
              <S.Action
                key={action.name}
                onClick={() => handleActionClick(action)}
                align="center"
              >
                <Flex flex="0 0 20px" align="center">
                  <S.SVGIcon>
                    {IconComponent && (
                      <IconComponent fill="#fff" stroke="#fff" />
                    )}
                  </S.SVGIcon>
                </Flex>
                <span>{action.name}</span>
              </S.Action>
            );
          })}
        </S.Actions>
      </Popup>
    </S.Dropdown>
  );
};

export default AddActionsDropdown;
